<template>
  <v-dialog v-model="dialog" max-width="600px" persistent>
    <template v-slot:activator="{ on, attrs }">
      <v-btn color="primary" elevation="0" v-bind="attrs" v-on="on"><v-icon>mdi-plus</v-icon> Thêm học phí cơ bản </v-btn>
    </template>

    <BaseBodyDialog title="Học phí cơ bản" @close="close" @save="save" :loading="loading">
      <v-container class="pa-0 mt-6">
        <v-form ref="form" v-model="valid">
          <v-row dense>
            <v-col cols="12">
              <AutocompleteClass
                v-model="formData.class"
                label="Lớp học"
                :rules="[(v) => !!v || 'Vui lòng chọn lớp học']"
                outlined
                hide-details
                dense
                @change="onClassChange"
              />
            </v-col>

            <v-col cols="12" v-if="formData.class">
              <div v-if="classTimeRange.start && classTimeRange.end" class="text-caption mb-6">
                Thời gian học: {{ formatDate(classTimeRange.start) }} - {{ formatDate(classTimeRange.end) }} ({{
                  countLesson
                }}
                buổi)
              </div>
              <div v-else class="text-caption mb-6">
                Lớp này chưa được xếp lịch, vui lòng xếp lịch trước khi cấu hình học phí
              </div>
            </v-col>

            <v-col cols="6">
              <v-select
                v-model="formData.priceType"
                :items="priceTypes"
                label="Mức học phí"
                item-text="text"
                item-value="value"
                :rules="[(v) => !!v || 'Vui lòng chọn mức học phí']"
                outlined
                dense
              />
            </v-col>

            <v-col cols="6">
              <v-select
                v-model="formData.paymentType"
                :items="filteredPaymentTypes"
                label="Hình thức thu"
                item-text="text"
                item-value="value"
                :rules="[(v) => !!v || 'Vui lòng chọn hình thức thu']"
                outlined
                dense
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="formData.amount"
                :label="amountLabel"
                :rules="[
                  (v) => !!v || 'Vui lòng nhập số tiền',
                  (v) => v > 0 || 'Số tiền phải lớn hơn 0',
                  (v) => Number.isInteger(Number(v)) || 'Số tiền phải là số nguyên',
                  (v) => v <= 1000000000 || 'Số tiền không được vượt quá 1 tỷ đồng',
                ]"
                suffix="VNĐ"
                outlined
                dense
              />
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </BaseBodyDialog>
  </v-dialog>
</template>

<script>
import AutocompleteClass from '@/components/basic/input/AutocompleteClass'
import BaseBodyDialog from '@/components/basic/dialog/BaseBodyDialog'
import { mask } from 'vue-the-mask'
import { mapActions } from 'vuex'
import moment from 'moment'

export default {
  name: 'DialogClassSetup',

  components: {
    AutocompleteClass,
    BaseBodyDialog,
  },

  directives: {
    mask,
  },

  data() {
    return {
      dialog: false,
      valid: false,
      loading: false,
      formData: {
        class: null,
        priceType: null,
        paymentType: null,
        amount: null,
      },
      priceTypes: [
        { text: 'Theo buổi', value: 'per_session' },
        { text: 'Theo tháng', value: 'monthly' },
        { text: 'Theo 3 tháng', value: 'quarterly' },
        { text: 'Theo khóa', value: 'course' },
      ],
      paymentTypes: [
        { text: 'Thu trước', value: 'pre' },
        { text: 'Thu sau (theo thực tế)', value: 'post_actual' },
        { text: 'Thu sau (theo dự kiến)', value: 'post_expected' },
      ],
      classTimeRange: {
        start: null,
        end: null,
      },
      countLesson: 0,
    }
  },

  computed: {
    amountLabel() {
      const priceType = this.priceTypes.find((type) => type.value === this.formData.priceType)
      if (!priceType) return 'Số tiền (VND)'
      return `Số tiền (VND) / ${priceType.text.toLowerCase()}`
    },

    filteredPaymentTypes() {
      if (!this.formData.priceType) return []

      switch (this.formData.priceType) {
        case 'per_session':
          return this.paymentTypes
        case 'monthly':
        case 'quarterly':
          return this.paymentTypes.filter((type) => ['pre', 'post_expected'].includes(type.value))
        case 'course':
          return this.paymentTypes.filter((type) => type.value === 'pre')
        default:
          return []
      }
    },
  },

  watch: {
    'formData.priceType'() {
      // Reset payment type when price type changes
      this.formData.paymentType = null
    },
  },

  methods: {
    ...mapActions('lesson', ['fetchLessons']),

    formatDate(date) {
      return moment(date).format('DD/MM/YYYY')
    },

    async onClassChange(classData) {
      if (!classData) {
        this.classTimeRange = {
          start: null,
          end: null,
        }
        return
      }

      try {
        let lessons = await this.fetchLessons({
          class: classData.id,
        })
        lessons = lessons.sort((a, b) => a.index - b.index)
        if (lessons && lessons.length > 0) {
          this.classTimeRange = {
            start: lessons[0].date,
            end: lessons[lessons.length - 1].date,
          }
          this.countLesson = lessons.length
        } else {
          this.classTimeRange = {
            start: null,
            end: null,
          }
          this.countLesson = 0
        }
      } catch (error) {
        console.error('Error fetching lessons:', error)
      }
    },

    close() {
      this.dialog = false
      this.$refs.form.reset()
      this.classTimeRange = {
        start: null,
        end: null,
      }
    },

    save() {
      if (this.$refs.form.validate()) {
        if (!this.classTimeRange.start || !this.classTimeRange.end) {
          return
        }
        // Format amount to number before saving
        const amount = Number(this.formData.amount.replace(/,/g, ''))
        const payload = {
          ...this.formData,
          classId: this.formData.class.id,
          amount,
          classTimeRange: this.classTimeRange,
        }

        this.$emit('save', payload)
        this.close()
      }
    },
  },
}
</script>
